import { lazy, type ComponentType } from "react";
import appsData from "./apps.json";

// Each app is its own chunk - the JS doesn't ship until a user actually
// opens that app. Keeps the first-load weight low.
const Initiative = lazy(() => import("./initiative/Initiative"));
const Zapquake = lazy(() => import("./zapquake/Zapquake"));

export type AppStatus = "local" | "external" | "wip";

export type AppMeta = {
  slug: string;
  title: string;
  description?: string;
  /** Free-form attribution shown on the app card (e.g. "Suggested by Tny", "Built by Nifusion"). */
  byline?: string;
  status?: AppStatus;
  /** When true, the app takes over the screen on mobile (no breadcrumb, no footer). */
  fullscreen?: boolean;
  /** Free-form tags surfaced by the Apps page's category filter. */
  tags?: string[];
  /** External apps live here. Internal apps live in `componentRegistry` below. */
  href?: string;
  /** Gate the app behind sign-in. AppsHost renders a sign-in prompt instead of the component. */
  requiresAuth?: boolean;
};

type InternalAppEntry = AppMeta & {
  Component: ComponentType;
  href?: never;
};

type ExternalAppEntry = AppMeta & {
  href: string;
  Component?: never;
};

export type AppEntry = InternalAppEntry | ExternalAppEntry;

// To add a new app:
//   1. Drop the metadata into `apps.json` (slug + title + tags + status).
//   2. Build the component under `src/apps/<slug>/` and add a slug → Component
//      entry below. If you forget step 2, the app is silently skipped - the
//      JSON entry alone won't crash the registry.
const componentRegistry: Record<string, ComponentType> = {
  initiative: Initiative,
  zapquake: Zapquake,
};

export const apps: AppEntry[] = (appsData as AppMeta[]).flatMap(
  (meta): AppEntry[] => {
    if (meta.href) {
      return [{ ...meta, href: meta.href } as ExternalAppEntry];
    }
    const Component = componentRegistry[meta.slug];
    if (!Component) return []; // metadata present but component not wired yet
    return [{ ...meta, Component } as InternalAppEntry];
  }
);

/** Dedup + sort of every tag declared in apps.json. */
export const allTags: string[] = Array.from(
  new Set(apps.flatMap((a) => a.tags ?? []))
).sort();
